import { useCallback, useRef, useState } from 'react'

import { lotoEntryFromProposal } from './lotoReportProposal'
import type {
  LotoReport,
  LotoReportEntry,
  LotoReportScope,
} from './lotoReportSchemas'
import { useLotoReports } from './useLotoReports'

export type LotoProposalSaveState =
  | { status: 'idle' | 'saving' | 'failed' }
  | { status: 'saved'; reportId: string }

type UseLotoProposalSaveResult = {
  state: LotoProposalSaveState
  saveProposal: () => Promise<void>
}

export function useLotoProposalSave(
  scope: LotoReportScope,
  proposal: LotoReport
): UseLotoProposalSaveResult {
  const { save } = useLotoReports(scope, false)
  const [state, setState] = useState<LotoProposalSaveState>({
    status: 'idle',
  })
  const savingRef = useRef(false)
  const draftRef = useRef<LotoReportEntry>()
  const saveProposal = useCallback(async (): Promise<void> => {
    if (savingRef.current) return
    savingRef.current = true
    setState({ status: 'saving' })
    const entry = lotoEntryFromProposal(scope, proposal, draftRef.current)
    draftRef.current = entry
    try {
      await save(entry)
      setState({ status: 'saved', reportId: entry.id })
    } catch {
      setState({ status: 'failed' })
    } finally {
      savingRef.current = false
    }
  }, [proposal, save, scope])
  return { state, saveProposal }
}
